import { GetStaticPaths, InferGetStaticPropsType, NextPage } from 'next'
import { useEffect, useState } from 'react'

import { useApiData } from '@hozana/api/hooks/useApiData'
import { createGetStaticData, createGetStaticProps } from '@hozana/api/server/static'
import { useSelector } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { getFirstQueryValue } from '@hozana/router/functions'

import { FullPageDiv } from 'general/structure/FullPageDiv'
import { Page } from 'general/structure/Page'
import { NAMESPACE } from 'i18n/constants'
import { PAGE } from 'routes/pages'

import { EPrayerMeetingStatus } from 'modules/meeting/constants'
import { getPrayerMeetingsQuery } from 'modules/meeting/queries'
import { TPrayerMeeting } from 'modules/meeting/types'

import { getPrayerEventQuery } from '../../queries'
import { selectPrayerEvent, selectPrayerMeetings } from '../../selectors'
import { PrayerEventDisplayMap } from './PrayerEventDisplayMap'
import { PrayerEventInfo } from './PrayerEventInfo'
import { PrayerMeetingPanel } from './PrayerMeetingPanel'

export const getStaticProps = createGetStaticProps(
  PAGE.EVENT_MAP,
  ({ store, params }) => [
    'event:event.map.title',
    { title: selectPrayerEvent(store.getState(), Number(params.prayerEventId))?.title },
  ],
  async (ctx) => {
    const prayerEventId = Number(ctx.params.prayerEventId)
    return createGetStaticData(() => ({
      prayerEvent: { query: getPrayerEventQuery({ prayerEventId }), displayErrors: true },
      prayerMeeting: {
        query: getPrayerMeetingsQuery({ prayerEventId, status: EPrayerMeetingStatus.VALIDATED }),
        displayErrors: true,
      },
    }))(ctx)
  },
  { namespaces: [NAMESPACE.EVENT] },
)

export const getStaticPaths: GetStaticPaths = async () => ({ paths: [], fallback: 'blocking' })

export type TEventMapPageProps = InferGetStaticPropsType<typeof getStaticProps>

export const PrayerEventEventMapPage: NextPage<TEventMapPageProps> = () => {
  const { query, replace, pathname, isReady } = useRouter()
  const [clickedMeeting, setClickedMeeting] = useState<TPrayerMeeting>(null)

  const prayerEventId = parseInt(getFirstQueryValue(query.prayerEventId))

  const prayerEvent = useSelector((state) => selectPrayerEvent(state, prayerEventId))

  const [prayerMeetingIds] = useApiData(
    () => ({
      query: getPrayerMeetingsQuery({ prayerEventId, status: EPrayerMeetingStatus.VALIDATED }),
    }),
    [prayerEventId],
  )
  const prayerMeetings = useSelector((state) => selectPrayerMeetings(state, prayerMeetingIds))

  useEffect(() => {
    if (isReady) {
      setClickedMeeting(prayerMeetings.find((prayerMeeting) => prayerMeeting.id.toString() === query.meeting))
    }
  }, [query.meeting, prayerMeetings, isReady])

  return (
    isReady && (
      <Page>
        <FullPageDiv height="100vh">
          <PrayerMeetingPanel
            prayerMeeting={clickedMeeting}
            prayerEvent={prayerEvent}
            onPanelClose={() => {
              setClickedMeeting(undefined)
              const { meeting: _extracted, ...remainingQuery } = query
              replace({ pathname, query: { ...remainingQuery } }, undefined, {
                shallow: true,
              })
            }}
          />
          {!clickedMeeting && <PrayerEventInfo prayerEvent={prayerEvent} prayerMeetings={prayerMeetings} />}
          {clickedMeeting !== null && (
            <PrayerEventDisplayMap
              prayerMeetings={prayerMeetings}
              defaultCenter={
                clickedMeeting
                  ? { lat: clickedMeeting.placeAddress.latitude, lng: clickedMeeting.placeAddress.longitude }
                  : undefined
              }
            />
          )}
        </FullPageDiv>
      </Page>
    )
  )
}
